import {
    ReactElement,
    useMemo,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useImperativeHandle
} from 'react';
import { GeneralTeamSettingsSectionFormRef } from './general-team-settings-section';
import { Team, Contact } from '@/api';
import { useUpdateTeamAction } from '../actions';
import { useCompaniesTranslation, useCompanyPermissions } from '../hooks';
import {
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISelectInput,
    UISelectValue,
    UISpacer,
    UISwitch,
    UIText
} from '@/ui';
import { useNavigate } from 'react-router';
import { useCompanyContext } from '../providers';
import { blue } from '@/ui/styles';
import { useRegion } from '@/common/providers/region-provider';
import { Trans } from 'react-i18next';

type TeamTaskSettingsSectionProps = { team: Team };

export function TeamTaskSettingsSection(
    props: TeamTaskSettingsSectionProps
): ReactElement {
    const { team } = props;

    const formRef = useRef<GeneralTeamSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });

    const { company } = useCompanyContext();
    const { canViewBilling } = useCompanyPermissions();
    const hasArcAccess = useMemo(() => {
        return company.subscription?.type === 'GUARDIAN_PLUS';
    }, [company.subscription]);

    const navigate = useNavigate();

    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: {
        pin: boolean;
        arc: boolean;
        taskTimeMinMins: number;
        taskTimeMaxMins: number;
    }) {
        const { pin, arc, taskTimeMinMins, taskTimeMaxMins } = data;
        updateTeam
            .updateTeam({
                settings: {
                    pinEnabled: pin,
                    arcEnabled: arc,
                    taskTimeMinMins,
                    taskTimeMaxMins
                }
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    function onNavigateToBillingPress() {
        void navigate(`/company/${team.companyId}/settings/billing`);
    }

    return (
        <TeamTaskSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            canViewBilling={canViewBilling}
            ref={formRef}
            initialPin={team.settings.pinEnabled}
            initialArc={team.settings.arcEnabled}
            initialTaskTimeMinMins={team.settings.taskTimeMinMins}
            initialTaskTimeMaxMins={team.settings.taskTimeMaxMins}
            contacts={team.contacts}
            loading={updateTeam.loading}
            hasArcAccess={hasArcAccess}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
            onNavigateToBillingPress={onNavigateToBillingPress}
        />
    );
}

type TeamTaskSettingsSectionFormProps = {
    canEdit: boolean;
    canViewBilling: boolean;
    initialPin: boolean;
    initialArc: boolean;
    initialTaskTimeMinMins: number;
    initialTaskTimeMaxMins: number;
    contacts: Contact[];
    loading: boolean;
    hasArcAccess: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: {
        pin: boolean;
        arc: boolean;
        taskTimeMinMins: number;
        taskTimeMaxMins: number;
    }) => void;
    onNavigateToBillingPress: () => void;
};

type TeamTaskSettingsSectionFormRef = {
    resetForm: () => void;
};

// Generate time options from 15 minutes to 24 hours (1440 minutes) in 15-minute increments
function generateTimeOptions(): UISelectValue[] {
    const options: UISelectValue[] = [];
    for (let mins = 15; mins <= 1440; mins += 15) {
        const hours = Math.floor(mins / 60);
        const minutes = mins % 60;
        let label: string;

        if (hours === 0) {
            label = `${minutes}m`;
        } else if (minutes === 0) {
            label = hours === 1 ? '1h' : `${hours}h`;
        } else {
            label = `${hours}h ${minutes}m`;
        }

        options.push({
            value: mins.toString(),
            label
        });
    }
    return options;
}

function formatTimeDisplay(mins: number): string {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;

    if (hours === 0) {
        return `${minutes}m`;
    } else if (minutes === 0) {
        return hours === 1 ? '1h' : `${hours}h`;
    } else {
        return `${hours}h ${minutes}m`;
    }
}

const TeamTaskSettingsSectionForm = forwardRef<
    TeamTaskSettingsSectionFormRef,
    TeamTaskSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        canViewBilling,
        initialPin,
        initialArc,
        initialTaskTimeMinMins,
        initialTaskTimeMaxMins,
        contacts,
        loading,
        hasArcAccess,
        isEditing,
        setIsEditing,
        onSubmit,
        onNavigateToBillingPress
    } = props;

    const { t } = useCompaniesTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);
    const [pin, setPin] = useState(initialPin);
    const [arc, setArc] = useState(initialArc);
    const [taskTimeMinMins, setTaskTimeMinMins] = useState(
        initialTaskTimeMinMins
    );
    const [taskTimeMaxMins, setTaskTimeMaxMins] = useState(
        initialTaskTimeMaxMins
    );
    const [showEnableArcModal, setShowEnableArcModal] = useState(false);

    const timeOptions = useMemo(() => generateTimeOptions(), []);

    /**
     * PIN
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const checkPin = useCallback((_pin: boolean): string => {
        return '';
    }, []);
    const pinError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkPin(pin);
    }, [checkPin, pin, showRealTimeErrors]);

    /**
     * ARC
     */
    const checkArc = useCallback(
        (arc: boolean): string => {
            // If contacts are empty and we are enabling ARC, then we need to show an error
            if (arc && contacts.length === 0) {
                return t(
                    'components.team_task_settings_section.error_monitoring_requires_escalation'
                );
            }
            return '';
        },
        [contacts, t]
    );
    const arcError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkArc(arc);
    }, [checkArc, arc, showRealTimeErrors]);

    /**
     * Task Time Limits
     */
    const checkTaskTimeMinMins = useCallback(
        (min: number, max: number): string => {
            if (min < 15) {
                return t(
                    'components.team_task_settings_section.error_min_task_time'
                );
            }
            if (min > max) {
                return t(
                    'components.team_task_settings_section.error_min_above_max'
                );
            }
            return '';
        },
        [t]
    );

    const checkTaskTimeMaxMins = useCallback(
        (min: number, max: number): string => {
            if (max > 1440) {
                return t(
                    'components.team_task_settings_section.error_max_task_time'
                );
            }
            if (max < min) {
                return t(
                    'components.team_task_settings_section.error_max_below_min'
                );
            }
            return '';
        },
        [t]
    );

    const taskTimeMinMinsError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkTaskTimeMinMins(taskTimeMinMins, taskTimeMaxMins);
    }, [
        checkTaskTimeMinMins,
        taskTimeMinMins,
        taskTimeMaxMins,
        showRealTimeErrors
    ]);

    const taskTimeMaxMinsError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkTaskTimeMaxMins(taskTimeMinMins, taskTimeMaxMins);
    }, [
        checkTaskTimeMaxMins,
        taskTimeMinMins,
        taskTimeMaxMins,
        showRealTimeErrors
    ]);

    function submitForm() {
        setShowEnableArcModal(false);
        onSubmit?.({
            pin,
            arc,
            taskTimeMinMins,
            taskTimeMaxMins
        });
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        const hasErrors =
            checkPin(pin) ||
            checkArc(arc) ||
            checkTaskTimeMinMins(taskTimeMinMins, taskTimeMaxMins) ||
            checkTaskTimeMaxMins(taskTimeMinMins, taskTimeMaxMins);

        if (!hasErrors) {
            setShowRealTimeErrors(false);

            if (arc && !initialArc) {
                setShowEnableArcModal(true);
            } else {
                submitForm();
            }
        }
    };

    function resetForm() {
        setPin(initialPin);
        setArc(initialArc);
        setTaskTimeMinMins(initialTaskTimeMinMins);
        setTaskTimeMaxMins(initialTaskTimeMaxMins);
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const sectionTitle = t('components.team_task_settings_section.title');

    return (
        <UISection>
            {isEditing ? (
                <EditableForm
                    sectionTitle={sectionTitle}
                    pin={pin}
                    setPin={setPin}
                    pinError={pinError}
                    arc={arc}
                    setArc={setArc}
                    arcError={arcError}
                    taskTimeMinMins={taskTimeMinMins}
                    setTaskTimeMinMins={setTaskTimeMinMins}
                    taskTimeMinMinsError={taskTimeMinMinsError}
                    taskTimeMaxMins={taskTimeMaxMins}
                    setTaskTimeMaxMins={setTaskTimeMaxMins}
                    taskTimeMaxMinsError={taskTimeMaxMinsError}
                    timeOptions={timeOptions}
                    loading={loading}
                    canEdit={canEdit}
                    canViewBilling={canViewBilling}
                    hasArcAccess={hasArcAccess}
                    showEnableArcModal={showEnableArcModal}
                    setShowEnableArcModal={setShowEnableArcModal}
                    resetForm={resetForm}
                    handleSubmit={handleSubmit}
                    submitForm={submitForm}
                    onNavigateToBillingPress={onNavigateToBillingPress}
                />
            ) : (
                <NonEditableForm
                    sectionTitle={sectionTitle}
                    pin={pin}
                    arc={arc}
                    taskTimeMinMins={taskTimeMinMins}
                    taskTimeMaxMins={taskTimeMaxMins}
                    hasArcAccess={hasArcAccess}
                    onEditClick={() => {
                        setIsEditing(true);
                        setShowRealTimeErrors(false);
                    }}
                    onNavigateToBillingPress={onNavigateToBillingPress}
                    canEdit={canEdit}
                    canViewBilling={canViewBilling}
                />
            )}
        </UISection>
    );
});

function NonEditableForm(props: {
    sectionTitle: string;
    pin: boolean;
    arc: boolean;
    taskTimeMinMins: number;
    taskTimeMaxMins: number;
    hasArcAccess: boolean;
    onEditClick: () => void;
    onNavigateToBillingPress: () => void;
    canEdit: boolean;
    canViewBilling: boolean;
}): ReactElement {
    const {
        sectionTitle,
        pin,
        arc,
        taskTimeMinMins,
        taskTimeMaxMins,
        hasArcAccess,
        onEditClick,
        onNavigateToBillingPress,
        canEdit,
        canViewBilling
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <>
            <UISectionHeader
                title={sectionTitle}
                rightButtons={
                    canEdit
                        ? [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  onClick: onEditClick
                              }
                          ]
                        : []
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <PINInfo pinError={''} />

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UIText>
                                {pin ? (
                                    <UIText medium type="link">
                                        {t('shared.labels.enabled')}
                                    </UIText>
                                ) : (
                                    <UIText medium type="error">
                                        {t('shared.labels.disabled')}
                                    </UIText>
                                )}
                            </UIText>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <ARCInfo
                            hasArcAccess={hasArcAccess}
                            canViewBilling={canViewBilling}
                            arcError={''}
                            onNavigateToBillingPress={onNavigateToBillingPress}
                        />

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UIText>
                                {arc ? (
                                    <UIText medium type="link">
                                        {t('shared.labels.enabled')}
                                    </UIText>
                                ) : (
                                    <UIText medium type="error">
                                        {t('shared.labels.disabled')}
                                    </UIText>
                                )}
                            </UIText>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <TaskTimeLimitsInfo />

                        <div
                            style={{
                                display: 'flex',
                                flex: 1
                            }}
                        >
                            <UIText>
                                <UIText medium>
                                    {formatTimeDisplay(taskTimeMinMins)}
                                </UIText>{' '}
                                to{' '}
                                <UIText medium>
                                    {formatTimeDisplay(taskTimeMaxMins)}
                                </UIText>
                            </UIText>
                        </div>
                    </div>
                </div>
            </UISectionContent>
        </>
    );
}

function EditableForm(props: {
    sectionTitle: string;
    pin: boolean;
    setPin: (value: boolean) => void;
    pinError: string;
    arc: boolean;
    setArc: (value: boolean) => void;
    arcError: string;
    taskTimeMinMins: number;
    setTaskTimeMinMins: (value: number) => void;
    taskTimeMinMinsError: string;
    taskTimeMaxMins: number;
    setTaskTimeMaxMins: (value: number) => void;
    taskTimeMaxMinsError: string;
    timeOptions: UISelectValue[];
    loading: boolean;
    canEdit: boolean;
    canViewBilling: boolean;
    hasArcAccess: boolean;
    showEnableArcModal: boolean;
    setShowEnableArcModal: (value: boolean) => void;
    resetForm: () => void;
    handleSubmit: FormEventHandler;
    submitForm: () => void;
    onNavigateToBillingPress: () => void;
}): ReactElement {
    const {
        sectionTitle,
        pin,
        setPin,
        pinError,
        arc,
        setArc,
        arcError,
        taskTimeMinMins,
        setTaskTimeMinMins,
        taskTimeMinMinsError,
        taskTimeMaxMins,
        setTaskTimeMaxMins,
        taskTimeMaxMinsError,
        timeOptions,
        loading,
        canEdit,
        canViewBilling,
        hasArcAccess,
        showEnableArcModal,
        setShowEnableArcModal,
        resetForm,
        handleSubmit,
        submitForm,
        onNavigateToBillingPress
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader title={sectionTitle} />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <PINInfo pinError={pinError} />

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UISwitch
                                    checked={pin}
                                    onChange={setPin}
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <ARCInfo
                                hasArcAccess={hasArcAccess}
                                canViewBilling={canViewBilling}
                                arcError={arcError}
                                onNavigateToBillingPress={
                                    onNavigateToBillingPress
                                }
                            />

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UISwitch
                                    checked={arc}
                                    onChange={setArc}
                                    disabled={!hasArcAccess || !canEdit}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 16
                            }}
                        >
                            <TaskTimeLimitsInfo />

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 12
                                }}
                            >
                                <div
                                    style={{
                                        flex: '1 1 200px',
                                        minWidth: 200
                                    }}
                                >
                                    <UISelectInput
                                        label={t(
                                            'components.team_task_settings_section.min_duration_label'
                                        )}
                                        value={{
                                            value: taskTimeMinMins.toString(),
                                            label: formatTimeDisplay(
                                                taskTimeMinMins
                                            )
                                        }}
                                        options={timeOptions}
                                        onChange={(option) => {
                                            if (option) {
                                                setTaskTimeMinMins(
                                                    parseInt(option.value, 10)
                                                );
                                            }
                                        }}
                                        disabled={!canEdit}
                                        errorText={taskTimeMinMinsError}
                                        clearable={false}
                                    />
                                </div>

                                <div
                                    style={{
                                        flex: '1 1 200px',
                                        minWidth: 200
                                    }}
                                >
                                    <UISelectInput
                                        label={t(
                                            'components.team_task_settings_section.max_duration_label'
                                        )}
                                        value={{
                                            value: taskTimeMaxMins.toString(),
                                            label: formatTimeDisplay(
                                                taskTimeMaxMins
                                            )
                                        }}
                                        options={timeOptions}
                                        onChange={(option) => {
                                            if (option) {
                                                setTaskTimeMaxMins(
                                                    parseInt(option.value, 10)
                                                );
                                            }
                                        }}
                                        disabled={!canEdit}
                                        errorText={taskTimeMaxMinsError}
                                        clearable={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                <UISectionFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: loading
                                ? t('shared.labels.saving')
                                : t('shared.labels.save')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            disabled: loading,
                            onClick: resetForm
                        }
                    ]}
                />
            </form>
            <EnableArcModal
                isOpen={showEnableArcModal}
                onClose={() => {
                    setShowEnableArcModal(false);
                }}
                onSubmit={submitForm}
            />
        </>
    );
}

function PINInfo(props: { pinError: string }): ReactElement {
    const { pinError } = props;

    const { t } = useCompaniesTranslation();
    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 4
            }}
        >
            <UIText>
                {t('components.team_task_settings_section.pin_label')}
            </UIText>
            <UIText type="default2">
                {t('components.team_task_settings_section.pin_description')}
            </UIText>
            {pinError && (
                <div style={{ paddingTop: 4 }}>
                    <UIText variant="small" type="error">
                        {pinError}
                    </UIText>
                </div>
            )}
        </div>
    );
}

function ARCInfo(props: {
    hasArcAccess: boolean;
    canViewBilling: boolean;
    arcError: string;
    onNavigateToBillingPress: () => void;
}): ReactElement {
    const { t } = useCompaniesTranslation();
    const { hasArcAccess, canViewBilling, arcError, onNavigateToBillingPress } =
        props;

    const [showArcInfoModal, setShowArcInfoModal] = useState(false);

    const { resolvedRegion } = useRegion();
    const allowPDFDownload = resolvedRegion == 'eu';

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 4
                }}
            >
                <UIText>
                    {t('components.team_task_settings_section.arc_title')}
                    {!hasArcAccess && (
                        <UIText medium type="error">
                            {' '}
                            {t(
                                'components.team_task_settings_section.arc_upgrade_required'
                            )}
                        </UIText>
                    )}
                </UIText>
                <UIText type="default2">
                    {t('components.team_task_settings_section.arc_description')}
                    {hasArcAccess && allowPDFDownload && (
                        <>
                            {' '}
                            <a
                                style={{
                                    color: blue,
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setShowArcInfoModal(true);
                                }}
                            >
                                {t(
                                    'components.team_task_settings_section.download_guide'
                                )}
                            </a>
                            .
                        </>
                    )}
                </UIText>
                {!hasArcAccess && (
                    <div style={{ paddingTop: 4 }}>
                        <UIText type="default2" variant="small">
                            <Trans
                                ns="companies"
                                i18nKey="components.team_task_settings_section.arc_only_available"
                                components={{ b: <b /> }}
                            />
                            {canViewBilling && (
                                <>
                                    {' '}
                                    <Trans
                                        ns="companies"
                                        i18nKey="components.team_task_settings_section.arc_visit_billing"
                                        components={{
                                            a: (
                                                <span
                                                    style={{
                                                        color: blue,
                                                        textDecoration:
                                                            'underline',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={
                                                        onNavigateToBillingPress
                                                    }
                                                />
                                            )
                                        }}
                                    />
                                    {`${allowPDFDownload ? '' : '.'}`}
                                </>
                            )}
                            {allowPDFDownload && (
                                <>
                                    {' '}
                                    {t('shared.labels.or')}
                                    <a
                                        style={{
                                            color: blue,
                                            textDecoration: 'underline',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => {
                                            setShowArcInfoModal(true);
                                        }}
                                    >
                                        {t(
                                            'components.team_task_settings_section.download_guide_inline'
                                        )}
                                    </a>
                                    .
                                </>
                            )}
                        </UIText>
                    </div>
                )}
                {arcError && (
                    <div style={{ paddingTop: 4 }}>
                        <UIText variant="small" type="error">
                            {arcError}
                        </UIText>
                    </div>
                )}
            </div>
            <ArcInfoModal
                isOpen={showArcInfoModal}
                onClose={() => {
                    setShowArcInfoModal(false);
                }}
            />
        </>
    );
}

function TaskTimeLimitsInfo(): ReactElement {
    const { t } = useCompaniesTranslation();
    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 4
            }}
        >
            <UIText>
                {t('components.team_task_settings_section.task_limits_title')}
            </UIText>
            <UIText type="default2">
                {t(
                    'components.team_task_settings_section.task_limits_description'
                )}
            </UIText>
        </div>
    );
}

type EnableArcModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: () => void;
};

function EnableArcModal(props: EnableArcModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <EnableArcModalForm {...props} /> : <></>}
        </UIModal>
    );
}

function EnableArcModalForm(props: EnableArcModalProps): ReactElement {
    const { onClose, onSubmit } = props;

    const { t } = useCompaniesTranslation();

    const [confirmed, setConfirmed] = useState(false);

    return (
        <>
            <UIModalHeader
                title={t('components.team_task_settings_section.arc_ack_title')}
                subtitle={t(
                    'components.team_task_settings_section.arc_ack_message'
                )}
                subtitleProps={{
                    type: 'default2'
                }}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'space-between'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4
                        }}
                    >
                        <UIText>
                            {t(
                                'components.team_task_settings_section.acknowledgement_label'
                            )}
                        </UIText>
                        <UIText variant="small" type="default2">
                            {t(
                                'components.team_task_settings_section.acknowledgement_checkbox'
                            )}
                        </UIText>
                    </div>

                    <UISwitch checked={confirmed} onChange={setConfirmed} />
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        disabled: !confirmed,
                        text: t(
                            'components.team_task_settings_section.arc_confirm'
                        ),
                        onClick: onSubmit
                    },
                    {
                        text: t('shared.labels.cancel'),
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}

type ArcInfoModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

function ArcInfoModal(props: ArcInfoModalProps): ReactElement {
    const { isOpen, onClose } = props;

    const { t } = useCompaniesTranslation();
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen && (
                <>
                    <UIModalHeader
                        title={t(
                            'components.team_task_settings_section.arc_title'
                        )}
                    />
                    <UIModalContent>
                        <UIText type="default2">
                            <Trans
                                ns="companies"
                                i18nKey="components.team_task_settings_section.arc_response_centre"
                                components={{
                                    a: (
                                        <a
                                            href="https://www.safepointapp.com/guardianplus-response-centre"
                                            target="_blank"
                                            style={{ color: blue }}
                                        />
                                    )
                                }}
                            />
                        </UIText>
                        <UISpacer />
                        <UIText type="default2">
                            <Trans
                                ns="companies"
                                i18nKey="components.team_task_settings_section.arc_once_enabled"
                                components={{ b: <b />, b1: <b /> }}
                            />
                        </UIText>
                        <UISpacer />
                    </UIModalContent>
                    <UIModalFooter
                        actions={[
                            {
                                variant: 'primary',
                                text: t(
                                    'components.team_task_settings_section.download_pdf'
                                ),
                                onClick: () => {
                                    window.open(
                                        'https://cdn.safepointapp.com/pdfs/safepoint-and-adt-response-process.pdf',
                                        '_blank'
                                    );
                                }
                            },
                            {
                                text: t('shared.labels.cancel'),
                                onClick: onClose
                            }
                        ]}
                    />
                </>
            )}
        </UIModal>
    );
}
